/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';

import { useExtensionsState } from '../../../controllers/use-extensions-state';
import { Height, type Item } from '../../../types';
import type { ItemDimensions } from '../types';

export type CardsDimensions = {
	[key in Height]: ItemDimensions;
};

/**
 * This is deprecated and will be removed when clean up cards updates
 */
export const cardsDimensions: CardsDimensions = {
	[Height.SMALL]: {
		minWidth: 221,
		maxWidth: 380,
		height: 72,
	},
	[Height.MEDIUM]: {
		minWidth: 240,
		maxWidth: 308,
		height: 312,
	},
	[Height.LARGE]: {
		minWidth: 358,
		maxWidth: 640,
		height: 312,
	},
};

export const getCommonCardDimensions = (cardSize: Height): ItemDimensions => {
	return cardsDimensions[cardSize];
};

const styles = cssMap({
	wrapperStylesUpdated: {
		flex: 1,
		position: 'relative',
		listStyleType: 'none',
		marginTop: token('space.0'),
	},
	wrapperWithInlinePadding: {
		paddingInline: token('space.025'),
	},
});

type ItemOrientationWrapperProps = {
	ariaRole?: string;
	cardId: Item['cardId'];
	cardWidthOnFullRow: number;
	children: React.ReactElement;
	clearScrollToCard: () => void;
	extensionLocalId: string;
	hasInlinePadding?: boolean;
	index: number;
	isFirstRowFull: boolean;
	itemDimensions: ItemDimensions;
	requestScrollIndex: (index: number) => void;
	scrollToCard: boolean;
	setWidth?: React.Dispatch<React.SetStateAction<number>>;
	testId?: string;
};

export const CardOrientationWrapper = ({
	children,
	isFirstRowFull,
	cardWidthOnFullRow,
	setWidth,
	testId = 'card-wrapper',
	scrollToCard,
	clearScrollToCard,
	index,
	requestScrollIndex,
	cardId,
	extensionLocalId,
	itemDimensions,
	hasInlinePadding = false,
	ariaRole,
}: ItemOrientationWrapperProps): JSX.Element => {
	const cardRef = useRef<HTMLDivElement>(null);
	const [{ selectedExtensionLocalId, selectedToEditCardId }] = useExtensionsState();
	const cardDimensions = itemDimensions;

	useEffect(() => {
		const scrollIntoViewBehavior: ScrollIntoViewOptions = {
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		};
		if (cardRef.current) {
			// Scroll to this card as long as its config panel is open OR no config panel is open.
			// If no config panel is open, that covers the case in edit mode where user has clicked
			// scroll buttons on a row-oriented cards instance which they aren't actively editing.
			if (
				scrollToCard &&
				(!selectedExtensionLocalId || extensionLocalId === selectedExtensionLocalId)
			) {
				cardRef.current.scrollIntoView(scrollIntoViewBehavior);

				// Clear scrollIndex to prevent re-invoking scroll effect later unexpectedly
				clearScrollToCard();
			}
			// Handle the case where user has selected this card to edit from the config panel.
			if (
				selectedToEditCardId &&
				selectedToEditCardId === cardId &&
				extensionLocalId === selectedExtensionLocalId
			) {
				// Now that we've reached the right card that user is selecting to edit,
				// trigger the scrolling hook to recalculate scroll window and index so
				// that we can rerender invoking the scroll effect
				requestScrollIndex(index);
			} else if (!selectedToEditCardId) {
				// if no card is selected to edit, set index to -1 indicate that it is not currently being used
				requestScrollIndex(-1);
			}
		}
	}, [
		cardRef,
		cardId,
		extensionLocalId,
		index,
		scrollToCard,
		clearScrollToCard,
		selectedExtensionLocalId,
		selectedToEditCardId,
		requestScrollIndex,
	]);

	return (
		<div
			role={ariaRole}
			css={[styles.wrapperStylesUpdated, hasInlinePadding && styles.wrapperWithInlinePadding]}
			style={{
				minWidth: isFirstRowFull ? `${cardWidthOnFullRow}px` : `${cardDimensions.minWidth}px`,
				maxWidth: isFirstRowFull ? `${cardWidthOnFullRow}px` : `${cardDimensions.maxWidth}px`,
			}}
			ref={cardRef}
			data-testid={testId}
		>
			{children}
			{index === 0 && setWidth && <WidthObserver setWidth={setWidth} offscreen />}
		</div>
	);
};
